import { useCallback, useEffect, useState } from 'react';
import { revenueApi, formatCurrency } from '../utils/api';
import { formatIsoRange, isoDaysBetween } from '../utils/revenueFormat';
import { formatDateShort } from '../utils/dateFormat';
import RevenueEntryForm from './RevenueEntryForm';
import RevenueCoverageView from './RevenueCoverageView';
import ConfirmModal from './ConfirmModal';
import Icon from './Icon';
import ExpandCaret from './ExpandCaret';

// הכנסות — the fourth section of קטגוריות: the entry form (a period and ONE
// amount), a collapsible coverage strip, and the entries list. Write for
// canEdit; read-only for viewers.
//
// The form comes FIRST now. It used to sit under the coverage calendar because
// the calendar was how you picked which category and range to type into; with
// one amount for the restaurant, entering this month's Z-report is the common
// case and should be the first thing under the heading.
//
// Retroactive editing is a NORMAL flow — any entry, however old, can be edited
// or deleted; there is no period locking. The widget reads revenue at query
// time, so a fix shows up on /analysis immediately.

export default function RevenueEntrySheet({ canEdit = false, open = true, onToggle, vatRate = 18 }) {
  const [entries, setEntries] = useState([]);
  const [loadError, setLoadError] = useState('');
  // Bumped after every write so the coverage view and the list both refetch.
  const [version, setVersion] = useState(0);
  const [prefill, setPrefill] = useState(null);   // {dateFrom, dateTo} from the calendar
  const [editing, setEditing] = useState(null);   // an entry row being edited
  const [confirm, setConfirm] = useState(null);
  // A typed-but-unsaved amount — collapsing the section would throw it away,
  // so the toggle asks first.
  const [formDirty, setFormDirty] = useState(false);
  // Collapsed by default: the form above is why most visits happen, and a
  // growing history table pushed it (and the coverage view) further down
  // every month.
  const [entriesOpen, setEntriesOpen] = useState(false);

  const refresh = useCallback(() => setVersion(v => v + 1), []);

  useEffect(() => {
    if (!open) return;
    let stale = false;
    setLoadError('');
    revenueApi.entries()
      .then(r => { if (!stale) setEntries(r.data || []); })
      .catch(e => { if (!stale) setLoadError(e?.response?.data?.error || 'שגיאה בטעינת ההכנסות'); });
    return () => { stale = true; };
  }, [open, version]);

  const removeEntry = (entry) => {
    setConfirm({
      title: 'מחיקת הזנת הכנסה',
      body: [
        `${formatIsoRange(entry.date_from, entry.date_to)} · ${formatCurrency(entry.amount)}`,
        'המחיקה תסיר את הימים האלה מהכיסוי — הם יסומנו כחסרים עד שיוזנו מחדש.',
      ],
      confirmLabel: 'מחיקת ההזנה',
      cancelLabel: 'ביטול',
      danger: true,
      onConfirm: async () => {
        setConfirm(null);
        try { await revenueApi.delete(entry.id); refresh(); }
        catch { setLoadError('שגיאה במחיקת ההזנה'); }
      },
    });
  };

  const onSaved = () => { setPrefill(null); setEditing(null); setFormDirty(false); refresh(); };

  // Collapsing the card unmounts the form — so when something is typed but
  // unsaved, the chevron asks instead of silently discarding it.
  const guardedToggle = () => {
    if (!formDirty) { onToggle?.(); return; }
    setConfirm({
      title: 'יש פדיון שלא נשמר',
      body: ['הקלדתם סכומים שעדיין לא נשמרו. סגירת המקטע תמחק אותם.'],
      confirmLabel: 'סגירה בלי לשמור',
      cancelLabel: 'חזרה לטופס',
      danger: true,
      onConfirm: () => { setConfirm(null); setFormDirty(false); onToggle?.(); },
    });
  };

  const dailyAvg = (e) => {
    const days = isoDaysBetween(e.date_from, e.date_to);
    return days > 1 ? `${formatCurrency(Math.round((e.amount / days) * 100) / 100)} ליום` : null;
  };

  return (
    <>
      {confirm && <ConfirmModal {...confirm} onCancel={() => setConfirm(null)} />}
      <section className={`card category-lines-card${open ? '' : ' is-collapsed'}`}>
        <button
          type="button" className="category-lines-toggle"
          onClick={guardedToggle} aria-expanded={open} aria-controls="revenue-sheet-body"
        >
          <span className="category-lines-title">הכנסות מהקופה (דוח Z)</span>
          <span className="category-lines-summary">
            {entries.length > 0 ? `${entries.length} הזנות` : ''}
          </span>
          <ExpandCaret open={open} size={14} />
        </button>

        {open && (
        <div id="revenue-sheet-body" dir="rtl">
          {loadError && (
            <div className="badge-red" style={{ display: 'block', marginBottom: 12, padding: '8px 12px', borderRadius: 8 }}>
              {loadError}
            </div>
          )}

          {/* 1. Entry. NOT keyed — a `key` here remounted the form on every
              calendar pick and silently threw away an amount already typed.
              The form syncs the range from props instead. */}
          {canEdit && (
            <div>
              <h3 style={{ margin: '0 0 2px', fontSize: 14 }}>הזנת פדיון</h3>
              <p style={{ margin: '0 0 10px', fontSize: 12, color: '#94a3b8' }}>
                סכום אחד מדוח ה־Z של הקופה, לתקופה שבחרתם
              </p>
              <RevenueEntryForm
                initialFrom={prefill?.dateFrom || ''}
                initialTo={prefill?.dateTo || ''}
                vatRate={vatRate}
                onSaved={onSaved}
                stickyActions
                // This form sits INSIDE a card, not directly in the page, so it
                // must not wear page chrome: `.docs-filter-wrap` paints
                // `var(--page-bg)` to mask content scrolling under a bar docked
                // beneath the navbar, and on a white card that just draws a grey
                // band across the picker's row. There is no navbar to dock
                // against here either. `stickyActions` stays — the save row is
                // action behaviour, deliberately independent of this.
                stickyFilterBar={false}
                onDirtyChange={setFormDirty}
              />
            </div>
          )}

          {/* 2. Coverage — which days have a Z-report and which do not. Tapping
              a day prefills the form above with that single day. */}
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #f0f0f0' }}>
            <RevenueCoverageView
              canEdit={canEdit}
              refreshToken={version}
              onPickRange={(pick) => setPrefill(pick)}
            />
          </div>

          {/* 3. The entries list — provenance is the point: the row is the
              thing the user typed, and edit/delete act on exactly it. Its own
              collapsible, closed by default: this is a history to check, not
              something every visit needs open. */}
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #f0f0f0' }}>
            <button
              type="button"
              onClick={() => setEntriesOpen(o => !o)}
              aria-expanded={entriesOpen} aria-controls="revenue-entries-list"
              style={{
                display: 'flex', alignItems: 'center', gap: 6, background: 'none',
                border: 'none', padding: 0, cursor: 'pointer', font: 'inherit',
              }}
            >
              <ExpandCaret open={entriesOpen} size={13} />
              <h3 style={{ margin: 0, fontSize: 14 }}>
                פדיון שכבר הוזן{entries.length > 0 ? ` (${entries.length})` : ''}
              </h3>
            </button>
            {entriesOpen && (
              <div id="revenue-entries-list" style={{ marginTop: 10 }}>
                {entries.length === 0 ? (
                  <p style={{ color: '#94a3b8', fontSize: 13 }}>עדיין לא הוזנו הכנסות.</p>
                ) : (
                  <div className="table-wrapper sheet-table" style={{ overflowY: 'auto', maxHeight: 300 }}>
                    <table>
                      <thead>
                        <tr>
                          <th>טווח</th>
                          <th>סכום (כולל מע״מ)</th>
                          <th>ממוצע יומי</th>
                          <th>הוזן ע״י</th>
                          {canEdit && <th style={{ width: 84 }} />}
                        </tr>
                      </thead>
                      <tbody>
                        {entries.map(e => (
                          <tr key={e.id}>
                            <td style={{ whiteSpace: 'nowrap' }}>{formatIsoRange(e.date_from, e.date_to)}</td>
                            <td style={{ whiteSpace: 'nowrap' }}>{formatCurrency(e.amount)}</td>
                            <td style={{ whiteSpace: 'nowrap', color: 'var(--ink-muted)' }}>{dailyAvg(e) || '—'}</td>
                            <td style={{ fontSize: 12, color: 'var(--ink-muted)' }}>
                              {e.created_by_email || '—'}
                              {e.created_at ? ` · ${formatDateShort(e.created_at)}` : ''}
                            </td>
                            {canEdit && (
                              <td style={{ whiteSpace: 'nowrap', padding: '6px 4px' }}>
                                <button type="button" className="btn btn-secondary"
                                  aria-label={`עריכת הזנה ${e.id}`} title="עריכה"
                                  onClick={() => setEditing(e)}
                                  style={{ padding: '4px 7px', marginInlineEnd: 4 }}>
                                  <Icon name="edit" size={13} />
                                </button>
                                <button type="button" className="btn btn-secondary"
                                  aria-label={`מחיקת הזנה ${e.id}`} title="מחיקה"
                                  onClick={() => removeEntry(e)}
                                  style={{ padding: '4px 7px', color: '#b91c1c' }}>
                                  <Icon name="trash" size={13} />
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* 4. Point fix — the shared single-entry form, in edit mode. */}
          {canEdit && editing && (
            <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid #f0f0f0' }}>
              <h3 style={{ margin: '0 0 10px', fontSize: 14 }}>
                עריכת הזנה · {formatIsoRange(editing.date_from, editing.date_to)}
              </h3>
              <RevenueEntryForm
                key={editing.id}
                entry={editing}
                vatRate={vatRate}
                onSaved={onSaved}
                onCancel={() => setEditing(null)}
              />
            </div>
          )}
        </div>
        )}
      </section>
    </>
  );
}
